// Client-side types and pure helpers for Cost Library Price Review.
import { samePurchaseUnit } from "@workspace/api-zod/cost-recipe";
// The generated contract types the document as a generic record, so these
// mirror the agreed response shapes and are narrowed at the call site.

export type Verdict = "Accurate" | "May need updating" | "Must be updated";
export type Decision = "pending" | "approved" | "rejected" | "deferred" | "applied";
export type FileKind = "prices" | "factors" | "notes";
export type ResearchType = "materials" | "labour";
export type ReviewTab = "review" | "materials" | "labour";

export interface CatalogueEntry {
  key: string; taskId: string; taskName: string; materialName: string; spec: string;
  unit: string; price: number | null; factor: number; waste: number; rowType: string; active: boolean;
}
export interface BatchSummary { id: string; name: string; createdAt: string; revision: number; rowCount: number; appliedCount: number; researchType?: ResearchType }
export interface LabourEntry { taskId: string; taskName: string; unit: string; active: boolean; labourAllowance: number | null }
export type HistoryType = "materials" | "labour" | "plant" | "work-item";
export interface HistoryEntry {
  id: string; taskId: string; taskName: string; itemName: string; type: HistoryType; field: string;
  previousPrice: number | null; updatedPrice: number | null; previousUnit: string | null; updatedUnit: string | null;
  appliedAt: string; actor: string | null; batchId: string | null; batchName: string | null; source: string | null;
  status: string; environment: string;
}
export interface ReviewSummary { pending: number; approved: number; completed: number; rejected: number; deferred: number }
export interface ReviewOverview {
  environment: "development" | "live"; canApply: boolean; batches: BatchSummary[]; catalogue: CatalogueEntry[]; labour?: LabourEntry[];
  history?: HistoryEntry[]; summary?: ReviewSummary;
}

/** Legacy batches without a researchType are materials. */
export const batchType = (b: BatchSummary): ResearchType => b.researchType === "labour" ? "labour" : "materials";
export const isLabourEntry = (c: CatalogueEntry) => c.rowType === "labour_allowance";

/** URL tab -> section. Old ?tab=prices maps to Materials; anything else opens Price Review. */
export function tabFromParam(raw: string | null): ReviewTab | "drafts" {
  if (raw === "drafts") return "drafts";
  if (raw === "materials" || raw === "prices") return "materials";
  if (raw === "labour") return "labour";
  return "review";
}

export interface Proposal {
  id: string; kind: "price" | "factor" | "labour"; targetKey: string | null; taskName: string; materialName: string; spec: string; unit: string;
  currentPrice: number | null; proposedPrice: number | null; proposedUnit: string;
  proposedFactor: number | null; proposedWaste: number | null; currentFactor: number | null; currentWaste: number | null;
  verdict: Verdict; researchVerdict?: string | null; confidence?: string | null; unresolved: boolean; issues: string[];
  evidence: Record<string, string>; decision: Decision; note?: string | null; contextConfirmed: boolean; pairedIds: string[];
}
export interface ImportPreview { rows: Proposal[]; warnings: string[]; fingerprint: string }
export interface BatchDetail { id: string; name: string; revision: number; rows: Proposal[]; files: { id?: string; name: string; kind: FileKind; downloadUrl?: string }[]; warnings?: string[]; history: Record<string, unknown>[] }
export interface Rates { low: number; typical: number; high: number; labour: number; plant: number; materials: number }
export interface ApplyPreview { token: string; impacts: { taskId: string; name: string; before: Rates; after: Rates }[]; currentTargets: CatalogueEntry[]; selectedIds: string[]; warnings: string[] }

export const VERDICTS: Verdict[] = ["Accurate", "May need updating", "Must be updated"];
export const DECISIONS: Decision[] = ["pending", "approved", "rejected", "deferred", "applied"];
export const DECISION_LABELS: Record<Decision, string> = { pending: "Pending", approved: "Approved", rejected: "Rejected", deferred: "Deferred", applied: "Applied" };

export const PRICE_TEMPLATE_HEADERS = [
  "material_id", "material_name", "spec", "material_unit", "current_unit_price", "price_verdict", "recommended_unit_price",
  "recommended_unit", "supplier", "source_url", "price_ex_vat", "source_date", "second_source_url", "second_price_ex_vat",
  "second_source_date", "reason", "confidence",
] as const;
export const REQUIRED_PRICE_COLUMNS = ["material_id", "spec", "material_unit", "current_unit_price", "material_name", "price_verdict"];

export const LABOUR_TEMPLATE_HEADERS = [
  "task_id", "task_name", "task_unit", "current_labour_allowance", "price_verdict", "recommended_labour_allowance", "recommended_unit",
  "source_url", "source_date", "price_ex_vat", "confidence", "supplier", "reason", "research_unit", "productivity_note",
] as const;
export const REQUIRED_LABOUR_COLUMNS = ["task_id", "task_name", "task_unit", "current_labour_allowance", "price_verdict"];

export function templateHeaders(t: ResearchType = "materials"): readonly string[] { return t === "labour" ? LABOUR_TEMPLATE_HEADERS : PRICE_TEMPLATE_HEADERS; }
export function requiredColumns(t: ResearchType = "materials"): string[] { return t === "labour" ? REQUIRED_LABOUR_COLUMNS : REQUIRED_PRICE_COLUMNS; }
export function templateCsv(t: ResearchType = "materials"): string { return templateHeaders(t).join(",") + "\n"; }

/** Labour research quoted per hour/day/crew rather than per work-item unit. Never converted silently. */
export function labourBasisDiffers(p: Proposal): boolean {
  if (p.kind !== "labour") return false;
  const ru = (p.evidence?.research_unit ?? "").trim().toLowerCase();
  return !!ru && ru !== (p.unit ?? "").trim().toLowerCase();
}

/** Header row of a CSV, honouring quoted fields. */
export function parseCsvHeader(text: string): string[] {
  const line = (text.replace(/^\uFEFF/, "").split(/\r?\n/).find((l) => l.trim() !== "") ?? "");
  const out: string[] = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) { if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; } else if (c === '"') q = false; else cur += c; }
    else if (c === '"') q = true; else if (c === ",") { out.push(cur.trim()); cur = ""; } else cur += c;
  }
  if (line) out.push(cur.trim());
  return out.filter((h) => h !== "");
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
/** Exact (normalised) header matches only; anything else stays unmapped for the admin to choose. */
export function autoMapping(headers: string[], t: ResearchType = "materials"): Record<string, string> {
  const m: Record<string, string> = {};
  for (const c of templateHeaders(t)) { const h = headers.find((x) => norm(x) === norm(c)); if (h) m[c] = h; }
  return m;
}

/** Only https URLs are rendered as links. */
export function safeHttpsUrl(v: string | null | undefined): string | null {
  if (!v) return null;
  try { const u = new URL(v.trim()); return u.protocol === "https:" ? u.toString() : null; } catch { return null; }
}

export function priceDiff(current: number | null, proposed: number | null): { abs: number; pct: number | null } | null {
  if (current == null || proposed == null) return null;
  return { abs: proposed - current, pct: current === 0 ? null : ((proposed - current) / current) * 100 };
}

export const gbp = (n: number | null | undefined) => n == null || Number.isNaN(n) ? "—" : `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
export const signedGbp = (n: number) => `${n > 0 ? "+" : n < 0 ? "-" : ""}${gbp(Math.abs(n))}`;
export const signedPct = (n: number | null) => n == null ? "n/a" : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;

export function retainsExistingPrice(p: Proposal): boolean {
  return p.kind === "price" && p.proposedPrice == null && samePurchaseUnit(p.proposedUnit, p.unit)
    && (p.researchVerdict || p.verdict).toLowerCase() === "accurate";
}

export function proposalUpdateLabel(p: Proposal): string {
  return p.kind === "factor" ? "Quantity / waste" : p.kind === "labour" ? "Labour price" : "Material price";
}

export function priceRecommendation(p: Proposal): string {
  return retainsExistingPrice(p) ? "Keep existing price" : p.proposedPrice == null ? "No proposed price" : gbp(p.proposedPrice);
}

/** Extra context is required for unit conversions, not research completeness. */
export function needsContext(p: Proposal, proposedUnit: string): boolean {
  return labourBasisDiffers(p) || (!!proposedUnit && (p.kind === "labour"
    ? proposedUnit.trim() !== (p.unit ?? "").trim()
    : !samePurchaseUnit(proposedUnit, p.unit ?? "")));
}

/** Plain-language purchase context derived only from what the evidence states. */
export function purchaseContext(e: Record<string, string>): { vat: string; delivery: string; pack: string } {
  const get = (...keys: string[]) => keys.map((k) => e[k]).find((v) => v != null && String(v).trim() !== "");
  const exVat = get("price_ex_vat", "second_price_ex_vat");
  const incVat = get("price_inc_vat");
  const delivery = get("delivery", "delivery_included", "delivery_cost");
  const pack = get("pack_size", "pack", "bulk", "quantity", "min_quantity", "purchase_basis");
  return {
    vat: exVat ? `Ex VAT (reported ${exVat})` : incVat ? `Inc VAT (reported ${incVat})` : "VAT basis not stated",
    delivery: delivery ? `Delivery: ${delivery}` : "Delivery unknown / not included",
    pack: pack ? `Pack or quantity basis: ${pack}` : "Pack, bulk and quantity basis not stated",
  };
}

export function asDoc<T>(v: unknown): T { return v as T; }

/** Admin evidence downloads: only same-origin API paths, resolved against the app base. */
export function safeFileUrl(u: string | null | undefined): string | null {
  if (!u || !/^\/api\/admin\/cost-library\/price-review\/[A-Za-z0-9_-]+\/files\/[A-Za-z0-9_-]+$/.test(u)) return null;
  const base = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
  return `${base}${u}`;
}

/** Reported evidence prices as a range; returns null if none are numeric. Not verified, may be noncomparable. */
export function reportedRange(e: Record<string, string>): { min: number; max: number; count: number } | null {
  const vals = ["price_ex_vat", "second_price_ex_vat"].map((k) => Number(String(e[k] ?? "").replace(/[^0-9.\-]/g, ""))).filter((n, i) => {
    const raw = e[["price_ex_vat", "second_price_ex_vat"][i]]; return raw != null && String(raw).trim() !== "" && Number.isFinite(n);
  });
  if (!vals.length) return null;
  return { min: Math.min(...vals), max: Math.max(...vals), count: vals.length };
}

/** Pairs not included in a selection (applied rows are already satisfied). */
export function missingPairIds(selected: Set<string>, rows: Proposal[]): string[] {
  const byId = new Map(rows.map((r) => [r.id, r])); const out = new Set<string>();
  for (const id of selected) for (const pid of byId.get(id)?.pairedIds ?? []) if (!selected.has(pid) && byId.get(pid)?.decision !== "applied") out.add(pid);
  return [...out];
}

/** Client-side mirror of approval preconditions for a row as saved. */
export function approvalBlocker(p: Proposal): string | null {
  if (!p.targetKey) return "No library target";
  if (p.kind !== "factor" && p.proposedPrice == null && !retainsExistingPrice(p)) return "No proposed price";
  if (needsContext(p, p.proposedUnit) && !p.contextConfirmed) return "Context not confirmed";
  if (needsContext(p, p.proposedUnit) && !(p.note ?? "").trim()) return "Review note required";
  return null;
}

/** Display-only: latest library price for the row's target, falling back to the stored snapshot. Never used for approval. */
export function libraryPrice(p: Proposal, byKey: Map<string, CatalogueEntry>): number | null {
  const c = p.targetKey ? byKey.get(p.targetKey) : undefined;
  return c && c.price != null ? c.price : p.currentPrice;
}

/** Original reported baseline from the CSV, or null when absent or non-numeric. */
export function reportedBaseline(e: Record<string, string> | undefined): number | null {
  const raw = e?.current_unit_price ?? e?.current_labour_allowance; if (raw == null || String(raw).trim() === "") return null;
  const n = Number(String(raw).replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) ? n : null;
}
